import {
  HttpClient,
  HttpParams,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { retryTransient } from '../utils/http-retry.utils';
import {
  CapacityReminderEmailPreference,
  CreateUserRequest,
  PagedResult,
  UpdateUserRequest,
  User,
  UserDto,
  UserListRequest,
  UserSearchRequest,
  UserSearchResult,
} from '../models/user.models';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private http = inject(HttpClient);
  private authService = inject(AuthService);

  private readonly apiUrl = `${environment.apiUrl}/api/users`;

  private buildUserListParams(request: UserListRequest): HttpParams {
    let params = new HttpParams()
      .set('page', request.page.toString())
      .set('pageSize', request.pageSize.toString());

    if (request.search) {
      params = params.set('search', request.search);
    }

    if (request.practiceAreaCode) {
      params = params.set('practiceAreaCode', request.practiceAreaCode);
    }

    if (request.isActive !== undefined) {
      params = params.set('isActive', request.isActive.toString());
    }

    if (request.roleId) {
      params = params.set('roleId', request.roleId.toString());
    }

    if (request.sortBy) {
      params = params.set('sortBy', request.sortBy);
    }

    if (request.sortDirection) {
      params = params.set('sortDirection', request.sortDirection);
    }

    return params;
  }

  /**
   * Get filtered and paginated list of users
   */
  getUsers(request: UserListRequest): Observable<PagedResult<UserDto>> {
    return this.http
      .get<PagedResult<UserDto>>(this.apiUrl, {
        params: this.buildUserListParams(request),
      });
  }

  /**
   * Get the merged User Management directory: application users plus the
   * employee/resource records the current user can access (by practice area).
   * Resource-only rows are flagged via UserDto.isResourceOnly and always read
   * as Basic + Inactive.
   */
  getUsersWithResources(
    request: UserListRequest,
  ): Observable<PagedResult<UserDto>> {
    return this.http
      .get<PagedResult<UserDto>>(`${this.apiUrl}/with-resources`, {
        params: this.buildUserListParams(request),
      });
  }

  /**
   * Get the total count of active Super Admins across the entire system
   * (not just the current page). Used to determine whether a Super Admin
   * is truly the last one remaining, independent of pagination.
   */
  getActiveSuperAdminCount(): Observable<number> {
    return this.http
      .get<{ count: number }>(`${this.apiUrl}/protected-admin-count`)
      .pipe(
        // Retry transient failures before surfacing an error to the caller; a 4xx fails at once
        retryTransient(2, 500),
        map((response) => response.count),
      );
  }

  /**
   * Get all users from the API (legacy method)
   */
  getAllUsers(): Observable<User[]> {
    return this.http.get<User[]>(`${this.apiUrl}/all`).pipe(
      map((users) => users.map((user) => this.transformUser(user))),
    );
  }

  /**
   * Get a specific user by ID
   */
  getUserById(id: number): Observable<User> {
    return this.http.get<User>(`${this.apiUrl}/${id}`).pipe(
      map((user) => this.transformUser(user)),
    );
  }

  /**
   * Create a new user
   */
  createUser(request: CreateUserRequest): Observable<User> {
    return this.http.post<User>(this.apiUrl, request).pipe(
      map((user) => this.transformUser(user)),
    );
  }

  /**
   * Update the current user's preferred notification email. Pass null to clear it.
   */
  updateMyPreferredEmail(preferredEmail: string | null): Observable<User> {
    return this.http.put<User>(`${this.apiUrl}/my/preferred-email`, { preferredEmail }).pipe(
      map((user) => this.transformUser(user)),
      tap((user) => {
        const current = this.authService.getCurrentUserValue();
        if (current && current.id === user.id) {
          this.authService.patchCurrentUser(user);
        }
      }),
    );
  }

  /**
   * Update the current user's email notification preference
   */
  updateMyEmailNotifications(enabled: boolean): Observable<User> {
    return this.http.put<User>(`${this.apiUrl}/my/email-notifications`, { emailNotificationsEnabled: enabled }).pipe(
      map((user) => this.transformUser(user)),
      tap((user) => {
        const current = this.authService.getCurrentUserValue();
        if (current && current.id === user.id) {
          this.authService.patchCurrentUser(user);
        }
      }),
    );
  }

  /**
   * Get the current user's resource capacity reminder email preference (stored opt-out + office-derived
   * visibility/lock).
   */
  getMyCapacityReminderEmailPreference(): Observable<CapacityReminderEmailPreference> {
    return this.http
      .get<CapacityReminderEmailPreference>(`${this.apiUrl}/my/capacity-reminder-email-preference`);
  }

  /**
   * Update the current user's resource capacity reminder email opt-out; returns the refreshed preference.
   */
  updateMyCapacityReminderEmails(enabled: boolean): Observable<CapacityReminderEmailPreference> {
    return this.http
      .put<CapacityReminderEmailPreference>(`${this.apiUrl}/my/capacity-reminder-emails`, {
        emailsEnabled: enabled,
      });
  }

  /**
   * Update an existing user
   */
  updateUser(id: number, request: UpdateUserRequest): Observable<User> {
    return this.http.put<User>(`${this.apiUrl}/${id}`, request).pipe(
      map((user) => this.transformUser(user)),
      tap((user) => {
        const current = this.authService.getCurrentUserValue();
        if (current && current.id === user.id) {
          this.authService.patchCurrentUser(user);
        }
      }),
    );
  }

  /**
   * Delete a user
   */
  deleteUser(id: number): Observable<boolean> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`).pipe(
      map(() => true),
    );
  }

  /**
   * Update a user's role
   */
  updateUserRole(userId: number, roleId: number): Observable<User> {
    const request = {
      userId: userId,
      roleId: roleId,
    };

    return this.http.put<User>(`${this.apiUrl}/role`, request).pipe(
      map((user) => this.transformUser(user)),
    );
  }

  /**
   * Search users with pagination and filtering
   */
  searchUsers(request: UserSearchRequest): Observable<UserSearchResult> {
    const params: any = {};

    if (request.query) params.query = request.query;
    if (request.excludePracticeAreaCode)
      params.excludePracticeAreaCode = request.excludePracticeAreaCode;
    if (request.pageIndex !== undefined) params.pageIndex = request.pageIndex;
    if (request.pageSize !== undefined) params.pageSize = request.pageSize;
    if (request.activeOnly !== undefined)
      params.activeOnly = request.activeOnly;

    return this.http
      .get<UserSearchResult>(`${this.apiUrl}/search`, { params })
      .pipe(
        map((result) => ({
          ...result,
          users: result.users.map((user) => this.transformUser(user)),
        })),
      );
  }

  /**
   * Transform user data from API format to frontend format
   * Handles date string conversion and null checks
   */
  private transformUser(user: any): User {
    return {
      id: user.id,
      email: user.email || '',
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      azureAdObjectId: user.azureAdObjectId,
      createdAt: user.createdAt,
      updatedAt: user.updatedAt,
      lastLogin: user.lastLogin,
      isActive: user.isActive ?? true,
      emailNotificationsEnabled: user.emailNotificationsEnabled ?? false,
      preferredEmail: user.preferredEmail ?? null,
      userRoles: user.userRoles || [],
    };
  }

  /**
   * Utility method to get user display name
   */
  getUserDisplayName(user: User): string {
    return `${user.firstName} ${user.lastName}`.trim();
  }

  /**
   * Utility method to format date for display
   */
  formatDate(dateString: string | null): string {
    if (!dateString) return 'N/A';

    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      }).format(date);
    } catch {
      return 'Invalid Date';
    }
  }

  /**
   * Utility method to check if API is available
   */
  healthCheck(): Observable<boolean> {
    return this.http
      .get<any>(`${environment.apiUrl}/health`, {
        observe: 'response',
      })
      .pipe(
        map((response) => response.status === 200),
      );
  }
}
